import { useState } from "react"
import { Box, Typography, CircularProgress } from "@mui/material"

import { useGetBulkLabelBatches } from "@app/hooks/useGetBulkLabelBatches"
import { useGetPermissions } from "@app/hooks/usePermissions"
import NoAccessPage from "@app/pages/NoAccess"
import { BatchDataGrid } from "./BatchDataGrid"

export const LastCreated: React.FC = () => {
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(25)
	const [sortBy, setSortBy] = useState<string | undefined>(undefined)
	const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(undefined)

	const { data: permissions, isLoading: isLoadingPermissions } = useGetPermissions()
	const canView = permissions?.can_view ?? true

	const { data, isLoading, isError } = useGetBulkLabelBatches({
		page,
		size: pageSize,
		sort_by: sortBy,
		sort_order: sortOrder,
		options: {
			enabled: canView,
		},
	})

	if (isLoadingPermissions) {
		return (
			<Box
				sx={{
					width: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<CircularProgress />
			</Box>
		)
	}

	if (!canView) {
		return <NoAccessPage />
	}

	if (isError) {
		return (
			<Box
				sx={{
					width: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<Typography variant="h6" color="error">
					Error loading bulk label batches. Please try again.
				</Typography>
			</Box>
		)
	}

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				minWidth: 0,
				flex: 1,
				p: 2,
			}}
		>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: "12px",
					minWidth: 0,
					minHeight: 0,
					flexDirection: "column",
					flex: 1,
					overflow: "hidden",
				}}
			>
				{isLoading ? (
					<Box
						sx={{
							width: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "400px",
						}}
					>
						<CircularProgress />
					</Box>
				) : data && data.bulk_label_batches.length === 0 ? (
					<Box sx={{ p: "10px", textAlign: "center" }}>
						<Typography variant="h6" fontWeight="bold">
							No Bulk Label Batches Found
						</Typography>
						<Typography variant="body2">Create some bulk labels to see them here!</Typography>
					</Box>
				) : (
					<BatchDataGrid
						batches={data?.bulk_label_batches ?? []}
						loading={isLoading}
						page={page}
						pageSize={pageSize}
						totalRows={data?.total ?? 0}
						sortBy={sortBy}
						sortOrder={sortOrder}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						onSortChange={(field, order) => {
							setSortBy(field)
							setSortOrder(order)
						}}
					/>
				)}
			</Box>
		</Box>
	)
}

export default LastCreated
